import React, { useState, useRef, useEffect } from "react";
import { Transition } from "utils";
import { InputLabel } from "../Inputs";

export interface IDropdownOption {
  label: string;
  value: string | number;
}
export interface DropdownProps {
  name: string;
  options: IDropdownOption[];
  onChange: (event: any) => void;
  value?: any;
  required?: boolean;
  label?: string;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
}

export const Dropdown = ({
  label,
  name,
  options,
  value,
  onChange,
  className,
  disabled,
  required,
  placeholder = "Wybierz...",
}: DropdownProps): JSX.Element => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selected, setSelected] = useState<number | string>(value ?? "");

  const trigger = useRef(null);
  const dropdown = useRef(null);

  useEffect(() => {
    onChange(selected);
  }, [selected]);

  useEffect(() => {
    setSelected(value);
  }, [value]);

  useEffect(() => {
    // @ts-ignore
    const clickHandler = ({ target }) => {
      if (!dropdown.current) return;
      if (
        !dropdownOpen ||
        // @ts-ignore
        dropdown.current.contains(target) ||
        // @ts-ignore
        trigger.current.contains(target)
      )
        return;
      setDropdownOpen(false);
    };
    document.addEventListener("click", clickHandler);
    return () => document.removeEventListener("click", clickHandler);
  });

  useEffect(() => {
    const keyHandler = ({ keyCode }: { keyCode: number }) => {
      if (!dropdownOpen || keyCode !== 27) return;
      setDropdownOpen(false);
    };
    document.addEventListener("keydown", keyHandler);
    return () => document.removeEventListener("keydown", keyHandler);
  });

  return (
    <div className={`relative inline-flex w-full ${label ? "flex-col" : ""} ${className ?? ""}`}>
      <InputLabel label={label} required={required} name={name} />
      <button
        ref={trigger}
        disabled={disabled}
        type="button"
        className={`btn h-9 min-w-full justify-between overflow-hidden border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:border-slate-600 dark:hover:text-slate-200 ${disabledClasses}`}
        onClick={() => setDropdownOpen(!dropdownOpen)}
        aria-expanded={dropdownOpen}
      >
        <span className="flex items-center whitespace-nowrap">
          <span>{options.find((x) => x.value == selected)?.label ?? placeholder}</span>
        </span>
        <svg className="ml-1 shrink-0 fill-current text-slate-400" width="11" height="7" viewBox="0 0 11 7">
          <path d="M5.4 6.8L0 1.4 1.4 0l4 4 4-4 1.4 1.4z" />
        </svg>
      </button>
      <Transition
        show={dropdownOpen}
        tag="div"
        className="absolute left-0 top-full z-10 mt-1 w-full overflow-hidden rounded border border-slate-200 bg-white py-1.5 shadow-lg dark:border-slate-700 dark:bg-slate-800"
        enter="transition ease-out duration-100 transform"
        enterStart="opacity-0 -translate-y-2"
        enterEnd="opacity-100 translate-y-0"
        leave="transition ease-out duration-100"
        leaveStart="opacity-100"
        leaveEnd="opacity-0"
      >
        <div
          ref={dropdown}
          className="max-h-80 overflow-y-auto overflow-x-hidden text-sm font-medium text-slate-600 dark:text-slate-300"
          onFocus={() => setDropdownOpen(true)}
          onBlur={() => setDropdownOpen(false)}
        >
          {options.map((option) => {
            return (
              <button
                key={option.value}
                tabIndex={0}
                type="button"
                className={`flex w-full cursor-pointer items-center px-3 py-1 hover:bg-slate-50 hover:dark:bg-slate-700/20 ${
                  option.value == selected && "text-indigo-500"
                }`}
                onClick={() => {
                  onChange(option.value);
                  setSelected(option.value);
                  setDropdownOpen(false);
                }}
              >
                <svg
                  className={`mr-2 shrink-0 fill-current text-indigo-500 ${option.value != selected && "invisible"}`}
                  width="12"
                  height="9"
                  viewBox="0 0 12 9"
                >
                  <path d="M10.28.28L3.989 6.575 1.695 4.28A1 1 0 00.28 5.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28.28z" />
                </svg>
                <span className="whitespace-nowrap">{option.label}</span>
              </button>
            );
          })}
        </div>
      </Transition>
    </div>
  );
};
const disabledClasses =
  "disabled:border-slate-200 dark:disabled:border-slate-700 disabled:bg-slate-100 dark:disabled:bg-slate-700 disabled:text-slate-400 dark:disabled:text-slate-600 disabled:cursor-not-allowed";
